import { createTask } from "../tasks/create.js";
import type { Task } from "../tasks/types.js";
import type { RecurrenceRule } from "../recurrence/types.js";
import type { RecurrenceOverride } from "../recurrence/overrides/types.js";
import { createOverride } from "../recurrence/overrides/types.js";
import { newId } from "../ids.js";
import { isoString, systemClock } from "../time.js";
import type { AssembleOptions, ImportAssembly, ImportedItem } from "./types.js";

/**
 * Turn parsed {@link ImportedItem}s into persistable rows. Groups by UID:
 * the master (no RECURRENCE-ID) becomes a base task (+ rule when it carries an
 * RRULE); EXDATEs become cancelled overrides; RECURRENCE-ID items become
 * single field overrides. Entity ids are resolved via caller-supplied hooks so
 * core stays IO-free. Items whose UID already exists locally are skipped.
 */
export function assembleImport(items: ImportedItem[], opts: AssembleOptions): ImportAssembly {
  const now = opts.now ?? systemClock;
  const out: ImportAssembly = { tasks: [], rules: [], overrides: [], skipped: 0 };

  for (const group of groupByUid(items)) {
    const uid = group[0]!.uid;
    if (uid && opts.existingUids?.has(uid)) { out.skipped++; continue; }

    const master = group.find((g) => !g.recurrenceId) ?? group[0]!;
    let recurrenceId: string | null = null;

    if (master.rrule && master.start) {
      const ts = isoString(now());
      const rule: RecurrenceRule = {
        id: newId(),
        rrule: master.rrule,
        dtstart: master.start.iso,
        until: master.until,
        tz: master.tz,
        exdates: [],
        created_at: ts,
        updated_at: ts,
        deleted_at: null,
      };
      recurrenceId = rule.id;
      out.rules.push(rule);
    }

    const base = createTask(toInput(master, recurrenceId, uid, opts), now);
    out.tasks.push({ ...base, status: master.status });

    if (recurrenceId) {
      for (const ex of master.exdates) {
        out.overrides.push(createOverride({ recurrence_id: recurrenceId, recurrence_id_ref: ex.iso, range: "single", status: "cancelled" }, now));
      }
      for (const item of group) {
        if (!item.recurrenceId) continue;
        out.overrides.push(overrideFor(item, recurrenceId, now, opts));
      }
    }
  }

  return out;
}

function toInput(m: ImportedItem, recurrenceId: string | null, uid: string | null, opts: AssembleOptions) {
  return {
    title: m.summary || (opts.emptySummaryTitle ?? "(untitled)"),
    notes: m.description,
    due_at: m.start?.iso ?? null,
    deadline_at: m.deadlineAt,
    all_day: m.allDay,
    duration_minutes: durationMinutes(m),
    priority: m.priority,
    custom_color: m.color,
    is_event: m.kind === "event" ? m.isEvent : false,
    location: m.location,
    label_ids: opts.resolveLabelIds(m.categories),
    topic_ids: opts.resolveTopicIds?.(m.topics) ?? [],
    calendar_id: opts.resolveCalendarId(m.calendarName),
    reminder_offsets: dedupeSort(m.reminders),
    tz: m.tz,
    ical_uid: uid,
    source: "ics-import",
    recurrence_id: recurrenceId,
  };
}

function overrideFor(
  item: ImportedItem,
  recurrenceId: string,
  now: typeof systemClock,
  opts: AssembleOptions,
): RecurrenceOverride {
  return createOverride(
    {
      recurrence_id: recurrenceId,
      recurrence_id_ref: item.recurrenceId!.iso,
      range: "single",
      status: item.status === "done" ? "done" : item.status === "cancelled" ? "cancelled" : "open",
      due_at: item.start?.iso ?? null,
      duration_minutes: item.durationMinutes ?? (durationMinutes(item) || null),
      title: item.summary || (opts.emptySummaryTitle !== undefined ? opts.emptySummaryTitle : null),
      notes: item.description || null,
      priority: item.priority === "none" ? null : item.priority,
      custom_color: item.color,
      all_day: item.allDay,
    },
    now,
  );
}

function durationMinutes(m: ImportedItem): number {
  if (m.durationMinutes != null) return m.durationMinutes;
  if (m.start && m.end) return Math.max(0, Math.round((new Date(m.end.iso).getTime() - new Date(m.start.iso).getTime()) / 60_000));
  return 0;
}

function dedupeSort(xs: number[]): number[] {
  return [...new Set(xs)].sort((a, b) => a - b);
}

/** Group items by UID; null-UID items each form their own singleton group. */
function groupByUid(items: ImportedItem[]): ImportedItem[][] {
  const byUid = new Map<string, ImportedItem[]>();
  const loose: ImportedItem[][] = [];
  for (const it of items) {
    if (!it.uid) { loose.push([it]); continue; }
    const g = byUid.get(it.uid);
    if (g) g.push(it);
    else byUid.set(it.uid, [it]);
  }
  return [...byUid.values(), ...loose];
}
